/**
 * Where the money went, account by account.
 *
 * The question a Finance Head opens the system to ask, and the one the reports
 * answer least directly: the trial balance gives a closing figure, the
 * cash-flow statement explains the change by activity, and neither says which
 * bank it went through.
 *
 * Two sources, side by side, because the gap between them is the point. The
 * ledger is the books. The statement column is what the bank said, including
 * lines nobody has posted. Showing only the first is accurate and quietly
 * misleading: 250 imported lines and an empty ledger look exactly like a month
 * with no transactions.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Loading, Note, Stat } from './ui'

interface Movement {
  bank_account_id: string
  name: string
  bank_name: string
  kind: string
  gl_account_code: string
  opening_paise: number
  in_paise: number
  out_paise: number
  net_paise: number
  closing_paise: number
  bank_in_paise: number
  bank_out_paise: number
  unposted_count: number
  agrees: boolean
}

interface CashMovement {
  since: string
  upto: string
  opening_paise: number
  in_paise: number
  out_paise: number
  closing_paise: number
  unposted_count: number
  accounts: Movement[]
}

export type WindowKey = 'fy' | '30' | '90' | '365'

/**
 * The financial year first, and the default. A rolling 90 days was the default
 * and it hid a real month: lines posted for 1 to 11 June fell before the
 * window's 17 June start, rolled silently into the opening balance, and the
 * dashboard showed nil in and nil out against a statement that plainly had
 * both. A finance reader asks "this year so far" far more often than "the last
 * 90 days", and a year-to-date window cannot drop the start of the year.
 */
const WINDOWS: { key: WindowKey; label: string }[] = [
  { key: 'fy', label: 'This financial year' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: '365', label: 'Last 12 months' },
]

/**
 * A calendar date in the viewer's own timezone. `toISOString()` is UTC, so
 * between midnight and 05:30 in India it returned yesterday, and a window
 * ending "today" quietly ended a day early for anybody working late.
 */
export function localIso(day: Date): string {
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const date = String(day.getDate()).padStart(2, '0')
  return `${day.getFullYear()}-${month}-${date}`
}

/** An Indian financial year starts on 1 April, so January to March belongs
 *  to the year that began the previous April. */
export function windowStart(key: WindowKey, today: Date): string {
  if (key === 'fy') {
    const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
    return `${startYear}-04-01`
  }
  const day = new Date(today)
  day.setDate(day.getDate() - Number(key))
  return localIso(day)
}

export function CashPosition() {
  const [windowKey, setWindowKey] = useState<WindowKey>('fy')
  const today = new Date()
  const since = windowStart(windowKey, today)
  const upto = localIso(today)

  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-movement', since, upto],
    queryFn: () => api.get<CashMovement>(`/banking/cash-movement?since=${since}&upto=${upto}`),
  })

  if (isLoading) return <Loading what="Working out the cash position" />
  if (error) return <ErrorNote error={error} />
  if (!data || data.accounts.length === 0) {
    return (
      <Note kind="info">
        No bank accounts yet, so there is no cash to report on. An administrator adds them
        under <Link to="/books/banking">Bank statements</Link>.
      </Note>
    )
  }

  return (
    <>
      <div className="grid">
        <Stat
          label="Cash in"
          value={formatInr(data.in_paise)}
          note={`across ${data.accounts.length} account${data.accounts.length === 1 ? '' : 's'}`}
        />
        <Stat label="Cash out" value={formatInr(data.out_paise)} note="posted to the books" />
        <Stat
          label="Net movement"
          value={`${data.in_paise - data.out_paise < 0 ? '−' : ''}${formatInr(Math.abs(data.in_paise - data.out_paise))}`}
          note={
            data.in_paise === 0 && data.out_paise === 0
              ? 'nothing moved in this period'
              : data.in_paise > data.out_paise
                ? 'more came in than went out'
                : data.in_paise < data.out_paise
                  ? 'more went out than came in'
                  : 'in and out were equal'
          }
        />
        <Stat
          label="Cash held"
          value={formatInr(data.closing_paise)}
          note={`opened at ${formatInr(data.opening_paise)}`}
        />
      </div>

      {data.in_paise === 0 && data.out_paise === 0 && data.opening_paise !== 0 && (
        <Note kind="info">
          Nothing was posted between {data.since} and {data.upto}. Anything posted before{' '}
          {data.since} is counted in the opening balance of {formatInr(data.opening_paise)}, so
          choose a longer period to see it as money in and out.
        </Note>
      )}

      {data.unposted_count > 0 && (
        <Note kind="warn">
          {data.unposted_count} imported line{data.unposted_count === 1 ? '' : 's'} have not
          been posted, so the figures above are not the whole story yet. The statement
          columns below show what the bank said; the rest shows what the books have.
        </Note>
      )}

      <Card
        title="By bank account"
        hint={`${data.since} to ${data.upto}`}
        tight
        actions={
          <select
            value={windowKey}
            onChange={(e) => setWindowKey(e.target.value as WindowKey)}
            style={{ width: 'auto' }}
          >
            {WINDOWS.map((w) => (
              <option key={w.key} value={w.key}>
                {w.label}
              </option>
            ))}
          </select>
        }
      >
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Bank</th>
              <th className="num">Opening</th>
              <th className="num">In</th>
              <th className="num">Out</th>
              <th className="num">Held now</th>
              <th className="num">Per statement</th>
            </tr>
          </thead>
          <tbody>
            {data.accounts.map((a) => (
              <tr key={a.bank_account_id}>
                <td>
                  {a.name}
                  <div className="faint mono">{a.gl_account_code}</div>
                </td>
                <td className="muted">{a.bank_name}</td>
                <td className="num muted">{formatInr(a.opening_paise)}</td>
                <td className="num">{a.in_paise ? formatInr(a.in_paise) : <span className="faint">—</span>}</td>
                <td className="num">{a.out_paise ? formatInr(a.out_paise) : <span className="faint">—</span>}</td>
                <td className="num">
                  <strong>{formatInr(a.closing_paise)}</strong>
                </td>
                <td className="num">
                  {a.unposted_count > 0 ? (
                    <>
                      <div className="muted">
                        +{formatInr(a.bank_in_paise)} / −{formatInr(a.bank_out_paise)}
                      </div>
                      <div className="faint">{a.unposted_count} not posted</div>
                    </>
                  ) : a.agrees ? (
                    <span className="muted">agrees</span>
                  ) : (
                    <span className="faint">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>All accounts</td>
              <td className="num">{formatInr(data.opening_paise)}</td>
              <td className="num">{formatInr(data.in_paise)}</td>
              <td className="num">{formatInr(data.out_paise)}</td>
              <td className="num">{formatInr(data.closing_paise)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </Card>
    </>
  )
}
